import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { spvPoolsApi } from "@/api/spvPools";
import { useInvestorCommitments } from "@/hooks/capitalMarkets/useInvestors";
import { useSpvPoolList } from "@/hooks/capitalMarkets/useSpvPools";
import {
  INVESTOR_COMMITMENT_STATUSES,
  type InvestorCommitmentDto,
} from "@/types/investors";
import { CommitmentStatusBadge } from "../../shared/components";
import { formatCurrency, formatDate, humanizeStatus } from "../../shared/format";

export function CommitmentsTab({ investorId }: { investorId: string }) {
  const [statusFilter, setStatusFilter] = useState("");
  const commitmentsQuery = useInvestorCommitments({ investorId, pageSize: 200 });
  const poolsQuery = useSpvPoolList({ pageSize: 200 });

  const commitments = useMemo(
    () => commitmentsQuery.data?.data ?? [],
    [commitmentsQuery.data],
  );

  const poolName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of poolsQuery.data?.data ?? []) m.set(p.id, p.poolName);
    return m;
  }, [poolsQuery.data]);

  // Resolve tranche codes by fetching pool math for each distinct pool the
  // investor is committed to (perTrancheFill carries poolTrancheId → code).
  const poolIds = useMemo(
    () => [...new Set(commitments.map((c) => c.spvPoolId))],
    [commitments],
  );
  const mathQueries = useQueries({
    queries: poolIds.map((id) => ({
      queryKey: ["spv-pools", "math", id],
      queryFn: () => spvPoolsApi.math(id),
    })),
  });
  const trancheCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const q of mathQueries) {
      for (const t of q.data?.perTrancheFill ?? []) {
        m.set(t.poolTrancheId, t.trancheCode);
      }
    }
    return m;
  }, [mathQueries]);

  const rows = useMemo(
    () =>
      statusFilter
        ? commitments.filter((c) => c.status === statusFilter)
        : commitments,
    [commitments, statusFilter],
  );

  if (commitmentsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading commitments…</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">
          Commitments across all pools
        </h3>
        <Select
          aria-label="Filter by status"
          className="w-48"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {INVESTOR_COMMITMENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanizeStatus(s)}
            </option>
          ))}
        </Select>
      </div>
      {rows.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No commitments recorded for this investor yet.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Pool</TH>
              <TH>Tranche</TH>
              <TH>Committed</TH>
              <TH>Funded</TH>
              <TH>Commitment Date</TH>
              <TH>Funding Date</TH>
              <TH>Status</TH>
              <TH>Sub. Agreement</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((c: InvestorCommitmentDto) => (
              <TR key={c.id}>
                <TD>
                  <Link
                    to={`/capital-markets/pools/${c.spvPoolId}`}
                    className="font-semibold hover:text-gold-shadow"
                  >
                    {poolName.get(c.spvPoolId) ?? "View pool"}
                  </Link>
                </TD>
                <TD>{trancheCode.get(c.poolTrancheId) ?? "—"}</TD>
                <TD>{formatCurrency(c.committedAmount)}</TD>
                <TD>{formatCurrency(c.fundedAmount)}</TD>
                <TD>{formatDate(c.commitmentDate)}</TD>
                <TD>{formatDate(c.fundingDate)}</TD>
                <TD>
                  <CommitmentStatusBadge status={c.status} />
                </TD>
                <TD>
                  {c.subscriptionAgreementDocumentId ? (
                    <FileText
                      size={16}
                      className="text-gold-shadow"
                      aria-label="Subscription agreement on file"
                    />
                  ) : (
                    <span className="text-gray-mid">—</span>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
